import { DocumentNode, useQuery } from "@apollo/client/react";
import {
  ContentBreadcrumbFragment_context,
  ContentStatus,
  FeedbackGivenOnType,
  GetMCParentFolder,
  GetMCParentFolderVariables,
} from "__generated__/types";
import classNames from "classnames";
import { useAuth } from "common/auth/Auth";
import { MultilineText, ofMarkdown } from "common/components/MultilineText";
import { NoDataError } from "common/components/NoDataError";
import { ResponsiveComponent } from "common/components/ResponsiveComponent";
import { SubText } from "common/components/Typography";
import { KurssloeyfeLayout } from "common/components/layouts/KurssloeyfeLayout";
import { LoadingLayout } from "common/components/layouts/LoadingLayout";
import { Markdown } from "common/components/markdown/Markdown";
import { useSimpleQuery } from "common/graphql/useSimpleQuery";
import { getSyllabus } from "common/hooks/useBreadcrumbs";
import { useMyExamSyllabus } from "common/hooks/useMyExamSyllabus";
import { FolderNavigation } from "common/navigation/FolderNavigation";
import { urls } from "common/urls";
import { Article } from "domain/article/Article";
import { folderWithElementsFragment } from "domain/common/queries";
import {
  getParentFromContentContext,
  isElementPublished,
  isSyllabusForStudent,
} from "domain/common/utils";
import { FeedbackForm } from "domain/feedback/FeedbackForm";
import { KurssloeyfeMobileMenu } from "domain/kurssloeyfe/KurssloeyfeMobileMenu";
import { KurssloeyfeSidebar } from "domain/kurssloeyfe/KurssloeyfeSidebar";
import {
  buildKurssloeyfeElementUrl,
  buildSelfAssessmentElementUrl,
} from "domain/kurssloeyfe/utils";
import { QuestionContainer } from "domain/multiple-choice/QuestionContainer";
import styles from "domain/pages/ElementPage.module.css";
import { KurssloeyfeRouteProps } from "domain/pages/KurssloeyfeElementPage";
import gql from "graphql-tag";
import React, { createContext, useContext } from "react";
import { Link, useParams } from "react-router-dom";

const GET_PARENT_QUERY = gql`
  query GetKurssloeyfeParentFolder($folderId: ID!) {
    folderById(id: $folderId) {
      ...GetFolderElementsFragment
    }
  }
  ${folderWithElementsFragment}
`;

export type ContainerElement = {
  id: string;
  content: {
    id: string;
    title: string;
    status: ContentStatus;
    youtubeVideoId?: string | null;
    __typename: string;
    context: ContentBreadcrumbFragment_context[];
    body?: {
      content: string;
      isMarkdown: boolean;
    } | null;
    canCreateProgression?: {
      canPerform: boolean;
    };
    relevantContent?: {
      content: string;
    } | null;
  };
};

type Data = {
  context?: ContentBreadcrumbFragment_context[];
  title: string;
  elements: ContainerElement[];
  canUpdateDetails: {
    canPerform: boolean;
  };
  id?: string;
  originFolderParentId?: string;
  progression?: {
    allContentIds: string[];
    finishedContentIds: string[];
  };
};

export type ElementNavListItem = {
  title: string;
  containerId?: string;
  finished?: boolean;
  canCreateProgression?: boolean;
  contentId: string;
  id?: string;
  __typename: "Video" | "Article" | "MultipleChoice";
};

type Props<TData> = {
  containerTitle?: string;
  containerId: string;
  elementId?: string;
  query: DocumentNode;
  progress?: React.ComponentType<Data["progression"]>;
  containerType:
    | ContentBreadcrumbFragment_context["__typename"]
    | "SelfAssessment"
    | undefined;

  getData(data: TData): Data | null;

  canCreateProgression?(el: ContainerElement): boolean;

  onFinishedElement?(): void;
};

interface ElementContext {
  elements: ElementNavListItem[];
  containerType:
    | ContentBreadcrumbFragment_context["__typename"]
    | "SelfAssessment"
    | undefined;

  buildElementUrl(el: ElementNavListItem): string;

  selfAssessmentId?: string;
}

const ElementContext = createContext<ElementContext | null>(null);

export function useElements(): ElementContext {
  const context = useContext(ElementContext);
  if (!context) {
    throw new Error("Missing ElementsProvider");
  }
  return context;
}

const NextPage = () => {
  const { elements, buildElementUrl } = useElements();
  const { kurssloeyfeElementId } = useParams<KurssloeyfeRouteProps>();

  const currentIndex = elements.findIndex(
    ({ contentId }) => contentId === kurssloeyfeElementId,
  );

  return (
    <div className={styles.navigation}>
      {currentIndex > 0 ? (
        <Link to={buildElementUrl(elements[currentIndex - 1])}>
          Forrige element
        </Link>
      ) : (
        <div />
      )}
      {currentIndex < elements.length - 1 && (
        <Link to={buildElementUrl(elements[currentIndex + 1])}>
          Neste element
        </Link>
      )}
    </div>
  );
};

export function ElementPage<TData>({
  containerId,
  elementId,
  query,
  getData,
  containerType,
  canCreateProgression,
  children,
}: React.PropsWithChildren<Props<TData>>): React.JSX.Element {
  const me = useAuth();
  const { myExamSyllabi } = useMyExamSyllabus();

  const { data, loadingOrError, refetch } = useSimpleQuery<
    TData,
    {
      containerId: string;
    }
  >(query, {
    variables: {
      containerId,
    },
  });

  const queryResult = data && getData(data);

  const parentId = getParentFromContentContext(queryResult?.context)?.id;

  const { data: folderData } = useQuery<
    GetMCParentFolder,
    GetMCParentFolderVariables
  >(GET_PARENT_QUERY, {
    skip: !parentId,
    variables: {
      folderId: parentId || "",
    },
  });

  if (loadingOrError) {
    return <LoadingLayout>{loadingOrError}</LoadingLayout>;
  }

  if (!queryResult) {
    return (
      <LoadingLayout>
        <NoDataError message="Vi fant dessverre ikke noe data for denne ID-en." />
      </LoadingLayout>
    );
  }

  const { title, context, canUpdateDetails, elements, id } = queryResult;

  const elementData =
    containerType === "SelfAssessment"
      ? elements.find((item) => item.id === elementId)
      : elements.find((item) => item.content.id === elementId);

  if (!elementData) {
    return (
      <LoadingLayout>
        <NoDataError message="Vi fant dessverre ikke denne artikkelen/oppgaven." />
      </LoadingLayout>
    );
  }

  const { content } = elementData;

  const enrichedElements = elements
    .filter(isElementPublished)
    .map<ElementNavListItem>((el) => ({
      contentId: el.content.id,
      id: el.id,
      containerId,
      title: el.content.title,
      __typename:
        el.content.__typename === "Article"
          ? el.content.youtubeVideoId
            ? "Video"
            : el.content.__typename
          : "MultipleChoice",
      finished: !!queryResult.progression?.finishedContentIds.includes(
        el.content.id,
      ),
      canCreateProgression: !!canCreateProgression?.(el),
    }));

  const tabs = [];

  if (
    content.__typename === "Article" ||
    content.__typename === "MultipleChoice"
  ) {
    const relevantContent = content.relevantContent?.content;

    const markdownRelevantContent = relevantContent
      ? ofMarkdown(relevantContent)
      : null;

    if (markdownRelevantContent) {
      tabs.push({
        title: "Relevant innhold",
        content: <Markdown {...markdownRelevantContent} openLinksSeparately />,
      });
    }
  }

  const canGiveFeedback =
    me.me?.menu.feedback && elementData.id && containerType === "Kurssloeyfe"; // TODO: Handle feedback for selvtest as well

  const isSyllabus = isSyllabusForStudent(
    myExamSyllabi,
    getSyllabus(content.context),
  );

  const isSelfAssessment = containerType === "SelfAssessment";

  return (
    <ElementContext.Provider
      value={{
        elements: enrichedElements,
        buildElementUrl: isSelfAssessment
          ? (el: ElementNavListItem) => buildSelfAssessmentElementUrl(el)
          : (el: ElementNavListItem) => buildKurssloeyfeElementUrl(el),
        containerType,
        selfAssessmentId: isSelfAssessment ? id : undefined,
      }}
    >
      <KurssloeyfeLayout context={context || []}>
        <section className={styles.grid}>
          <ResponsiveComponent
            threshold={1024}
            aboveThreshold={
              <div className={styles.sidebarContainer}>
                <KurssloeyfeSidebar
                  containerId={containerId}
                  canUpdateDetails={canUpdateDetails.canPerform}
                  title={title}
                  progress={children}
                />
                <FolderNavigation
                  intent="secondary"
                  currentElementId={containerId}
                  nextButtonText="Ferdig, gå til"
                  showPrevious={false}
                  folderElements={
                    folderData?.folderById?.elements.map((e) => ({
                      title: e.content.title,
                      __typename: e.content.__typename,
                      id: e.content.id,
                    })) ?? []
                  }
                />
              </div>
            }
            belowThreshold={
              <>
                <KurssloeyfeMobileMenu
                  title={title}
                  currentElementTitle={elementData.content.title}
                />
              </>
            }
          />

          <div>
            <NextPage />

            {content.__typename === "Article" && (
              <article className={classNames(styles.content, styles.article)}>
                <Article
                  title={content.title}
                  youtubeVideoId={content.youtubeVideoId}
                  tabs={tabs}
                  id={content.id}
                  containerType={containerType}
                  isSyllabus={
                    isSyllabus && content.canCreateProgression?.canPerform
                  }
                  onFinishedElement={() => refetch()}
                >
                  {content.body && (
                    <MultilineText
                      {...content.body}
                      maxWidth
                      openLinksSeparately
                    />
                  )}
                  {canGiveFeedback && (
                    <FeedbackForm
                      givenOnReferenceId={elementData.id}
                      givenOnType={FeedbackGivenOnType.KurssloeyfeElement}
                    />
                  )}
                  {canUpdateDetails.canPerform && (
                    <SubText>
                      <Link to={urls.articleAdminEdit(content.id)}>
                        Rediger artikkel
                      </Link>
                    </SubText>
                  )}
                </Article>
              </article>
            )}

            {content.__typename === "MultipleChoice" && (
              <article
                className={classNames(styles.content, styles.assignment)}
              >
                <QuestionContainer
                  multipleChoiceId={content.id}
                  tabs={tabs}
                  onFinishedElement={() => refetch()}
                  containerType={containerType}
                >
                  {canGiveFeedback && (
                    <FeedbackForm
                      givenOnReferenceId={elementData.id}
                      givenOnType={FeedbackGivenOnType.KurssloeyfeElement}
                    />
                  )}
                </QuestionContainer>
              </article>
            )}
          </div>
        </section>
        <ResponsiveComponent
          belowThreshold={
            <FolderNavigation
              intent="secondary"
              currentElementId={containerId}
              nextButtonText="Ferdig, gå til"
              showPrevious={false}
              folderElements={
                folderData?.folderById?.elements.map((e) => ({
                  title: e.content.title,
                  __typename: e.content.__typename,
                  id: e.content.id,
                })) ?? []
              }
            />
          }
          threshold={1024}
        />
      </KurssloeyfeLayout>
    </ElementContext.Provider>
  );
}
